import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GET_USER_DETAILS } from "../../../apollo/operations";
import {
  Loading,
  AdSidebar,
  AdDashboardNav,
  PerformanceBox,
} from "../../../components";
import { setUserDetails } from "../../../redux/slices/userSlice";
import { showToast } from "../../../utils/showToast";
import { useQuery } from "@apollo/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Datas } from "../../../constants/chart";

const CampaignOverview: React.FC = () => {
  const dashboardMenu = [
    {
      name: "Overview",
    },
    {
      name: "Get Started",
    },
    {
      name: "Ads",
    },
    {
      name: "Help",
    },
  ];

  const [active, setActive] = React.useState<string>(dashboardMenu[2].name);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userID = window.localStorage.getItem("user");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading, error, data, refetch } = useQuery(GET_USER_DETAILS, {
    variables: {
      where: {
        id: userID,
      },
    },
    onCompleted: (data) => {
      console.log(data.user);
      dispatch(setUserDetails(data.user));
    },
    onError: (error) => {
      console.log(error);

      showToast(error.message, "error");
      navigate("/auth");
    },
  });

  // Calculate total Men and Women
  const totalMen = Datas.reduce((acc, entry) => acc + entry.Men, 0);
  const totalWomen = Datas.reduce((acc, entry) => acc + entry.Women, 0);
  const totalResults = totalMen + totalWomen;

  if (loading) return <Loading loading={loading} />;
  return (
    <div className="overflow-x-hidden overscroll-none h-full pl-[19%] py-[18%]">
      <AdSidebar
        active={active}
        setActive={setActive}
        setIsOpen={setIsOpen}
        dashboardMenu={dashboardMenu}
      />
      {data && (
        <div className="w-full flex flex-col z-20 lg:fixed lg:left-full lg:bg-[#fbfafa]">
          <AdDashboardNav
            active={active}
            toggleSidebar={toggleSidebar}
            data={data}
            isOpen={isOpen}
          />
        </div>
      )}
      <div className="py-5 top-24 pl-[3.9%] pr-[8%] flex justify-between absolute left-[15%] right-0 bg-[#5F4AE514] items-center">
        <p className="text-black font-semibold text-lg font-Poppins">
          Orokii Campaign
        </p>
        <div className="flex w-[40%] bg-midGreen rounded justify-center items-center text-white font-Inter text-base font-medium h-[54px] ">
          This month: Jun 1, 2023 - Jun 14, 2023
        </div>
      </div>
      <div className="gap-6 flex flex-col font-Poppins pr-[10%]">
        <PerformanceBox />
        <div className="flex rounded-lg p-8 pr-[97px] text-[#555] font-Poppins flex-col shadow-[0px_4px_16px_0px_rgba(24,119,242,0.08)] bg-white">
          <h1 className="text-lg font-semibold mb-1">
            Compare how different ad creatives perform
          </h1>
          <p className="text-xs font-normal">
            You can get even more detailed performance by using ad creative
            breakdowns in Ads reporting. This can help you compare ad
            performance by image, text and call to action to see how your
            creatives performs across all placements.
          </p>
          <div className="mt-3 flex h-11 w-56 bg-[#CEC7F866] rounded justify-center items-center text-black text-sm font-semibold">
            Go to Ads Reporting
          </div>
        </div>
        <div className="flex rounded-lg p-8 text-[#555] font-Poppins flex-col gap-10 shadow-[0px_4px_16px_0px_rgba(24,119,242,0.08)] bg-white">
          <div className="flex gap-3">
            <div className="mt-3 flex h-11 w-56 bg-[#CEC7F866] rounded justify-center items-center text-midGreen text-sm font-semibold">
              Demographics
            </div>
            <div className="mt-3 flex h-11 w-56 bg-[#CEC7F866] rounded justify-center items-center text-black text-sm font-semibold">
              Platform
            </div>
          </div>
          <div className="flex justify-between items-center">
            <h1>Age and gender distribution</h1>
            <div className="flex gap-3">
              <div className="flex w-28 h-11 bg-[#CEC7F866] rounded justify-center items-center text-black text-sm font-semibold">
                All
              </div>
              <div className="flex w-28 h-11 bg-[#CEC7F866] rounded justify-center items-center text-black text-sm font-semibold">
                Results
              </div>
            </div>
          </div>
          <div>
            <BarChart
              width={900}
              height={300}
              data={Datas}
              margin={{
                top: 15,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid vertical={false} horizontal={true} />
              <XAxis dataKey="name" />
              <YAxis
                axisLine={false}
                tickFormatter={(tick: any) => (tick === 1000 ? "1k" : tick)}
                ticks={[0, 200, 400, 600, 800, 1000]}
                strokeWidth={0}
              />
              <Tooltip />
              <Bar dataKey="Men" fill="#5234B8" />
              <Bar dataKey="Women" fill="#4EC7C7" />
            </BarChart>

            <div className="flex gap-11 mt-10 ml-4">
              {["Men", "Women"].map((gender, index) => (
                <div className="flex gap-1" key={index}>
                  <div
                    className={`bg-${
                      gender === "Men" ? "[#5234B8]" : "[#4EC7C7]"
                    } w-4 h-4 rounded-sm shadow-sm`}
                  ></div>
                  <div className="text-[#555] font-Poppins text-sm font-semibold gap-1">
                    <h1>{gender}</h1>
                    <h1>{`${(
                      ((gender === "Men" ? totalMen : totalWomen) /
                        totalResults) *
                      100
                    ).toFixed(2)}% (${
                      gender === "Men" ? totalMen : totalWomen
                    })`}</h1>
                    <h1>
                      Cost per result: {gender === "Men" ? "$1.06" : "$0.91"}
                    </h1>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignOverview;
